import { TemplateId } from '@deltasierra/ids';
import { Activity, ActivityId } from '../activity';
import { CollectionId } from '../assetLibrary';
import { BuilderTemplateCategoryId, BuilderTemplateId } from '../builderTemplate';
import { Channel } from '../channel';
import { ClientId } from '../client';
import {
    ApiRoute,
    FullAuditable,
    HasDate,
    HasId,
    HttpMethod,
    RelativeUrl,
    clock,
    coerceDate,
    getDateAsUtc,
    subtractWeekdays,
} from '../common';
import { DeviceId } from '../devices';
import { LocationId } from '../location';
import { PublishedArtifact } from '../publishedArtifact';
import { Upload } from '../upload';
import { HasRoles, User, UserId, isManager } from '../user/User';
import { RecurringEventData } from './RecurringPlanner';

// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
export const PlannerStatuses = Object.freeze(<{ [K in PlannerStatus]: PlannerStatus }>{
    built: 'built',
    planned: 'planned',
    scheduled: 'scheduled',
});
export const PlannerStatusValues: PlannerStatus[] = ['built', 'planned', 'scheduled'];
export type PlannerStatus = 'built' | 'planned' | 'scheduled';

export interface GetPublishedArtifactsForPlannerParams {
    id: number;
}

export type PlannerPublishedArtifact = Pick<PublishedArtifact, 'channel' | 'id' | 'scheduledTime'> & {
    isPublished: boolean;
};

export type PlannerUserInfo = {
    createdBy: string | null;
    updatedBy: string | null;
};

export const PlannerApi = {
    getPublishedArtifacts: new ApiRoute<PlannerPublishedArtifact[], GetPublishedArtifactsForPlannerParams, void, void>(
        HttpMethod.Get,
        path => path.literal('planners').param('id').literal('published-artifacts'),
    ),
};

export interface Planner extends HasId, FullAuditable {
    title: string;
    date: Date;
    text: string | null;
    maxSpend: number | null;
    expectedLeads: number | null;
    postedBy: string;
    isSupported: boolean;
    contentSupplied: boolean;
    status: PlannerStatus;
    clientId: ClientId;
    locationId: LocationId;
    activityId: ActivityId | null;
    campaignRolloutId: number | null;
    recurringPlannerId: number | null;
    instructions: string | null;
    assignedToId: UserId | null;
    isArtifactRequired: boolean;
    collectionId?: CollectionId | null;
    builderTemplateCategoryId?: BuilderTemplateCategoryId | null;
    builderTemplateId?: BuilderTemplateId | null;
    buildableTemplateId?: TemplateId | null;
    deviceId: DeviceId | null;

    channels?: Channel[];
    uploads?: Upload[];
    activity?: Activity;
    assignedTo?: User;

    graphqlId: string;
}

export interface GetPlannerDto {
    buildableTemplateId?: string | null;
    planner: Planner;
    recurringEventData: RecurringEventData | null;
}

export type NewPlannerData = Pick<
    Planner,
    | 'activityId'
    | 'buildableTemplateId'
    | 'builderTemplateCategoryId'
    | 'builderTemplateId'
    | 'campaignRolloutId'
    | 'clientId'
    | 'collectionId'
    | 'contentSupplied'
    | 'date'
    | 'deviceId'
    | 'instructions'
    | 'isArtifactRequired'
    | 'isSupported'
    | 'locationId'
    | 'text'
    | 'title'
> & {
    maxSpend?: number | null;
    expectedLeads?: number | null;
    channels?: Channel[] | number[];
    uploads?: any[] | null;
    deletedUploads?: any[] | null;
    plannerId?: number;
};

export type CreatePlannerData = NewPlannerData & {
    recurringPlannerId?: number | null;
    postedBy: string;
    deletedUploads?: Upload[];
};

export type PlannerOverviewDto = Pick<
    Planner,
    | 'assignedToId'
    | 'clientId'
    | 'contentSupplied'
    | 'date'
    | 'id'
    | 'isArtifactRequired'
    | 'isSupported'
    | 'locationId'
    | 'maxSpend'
    | 'recurringPlannerId'
    | 'status'
    | 'title'
> & {
    location: string;
    timeOffset: string;
    client: string;
    channels: Array<{
        id: number;
        label: string;
        isSupported: boolean;
        isSelfService: boolean;
    }>;
};

export type GalleryPlannerDetails = Pick<
    Planner,
    'channels' | 'clientId' | 'date' | 'graphqlId' | 'id' | 'locationId' | 'text' | 'title' | 'uploads'
>;

export interface UpdatePlannerStatusBody {
    locationId: LocationId;
    plannerId?: number;
    recurringPlannerId?: number;
    date: Date;
    status: PlannerStatus;
}

export interface AssignPlannerBody {
    locationId: LocationId;
    plannerId?: number;
    recurringPlannerId?: number | null;
    userId: UserId | null;
    date: Date;
}

export interface DeletePlannerBody {
    locationId: LocationId;
    plannerId?: number | null;
    applyActionTo: ApplyActionTo;
    recurringPlannerId?: number | null;
    date?: Date | null;
}

export interface GetPlannerOverviewDetailsBody {
    locationId?: LocationId;
    plannerId?: number;
    recurringPlannerId?: number;
}

export interface GetPlannerUserInfoBody {
    plannerId?: number;
    recurringPlannerId?: number;
}

export type OverviewPlannerDetails = Pick<Planner, 'id' | 'instructions' | 'recurringPlannerId' | 'text'> & {
    uploads: Array<{
        url: string;
        filename: string;
    }>;
};

/**
 * Returns false if the planner date is falsey.
 *
 * @param planner
 */
export const forbiddenDateThreshold = 3;
export function isAfterSupportedPeriod(planner: HasDate): boolean {
    if (planner && planner.date) {
        const now = clock.now();
        const plannerDate = getDateAsUtc(coerceDate(planner.date));
        const latestDate = subtractWeekdays(plannerDate, forbiddenDateThreshold);
        return now > latestDate;
    } else {
        return false;
    }
}

type MinimalPlanner = Pick<Planner, 'channels' | 'date' | 'isArtifactRequired' | 'isSupported'>;

export function canSaveSupportedPlannerAsReadyToAction(user: HasRoles, planner: MinimalPlanner): boolean {
    return isManager(user) || !isSupported(planner) || !isAfterSupportedPeriod(planner);
}

function isChannelAvailable(planner: MinimalPlanner) {
    return (
        planner.channels &&
        planner.channels.length > 0 &&
        typeof planner.channels[0] === 'object' &&
        planner.channels[0] !== null
    );
}

export function isSelfService(planner: MinimalPlanner): boolean {
    if (!planner) {
        return false;
    } else if (isChannelAvailable(planner)) {
        return planner.channels![0].isSelfService;
    } else {
        return planner.isArtifactRequired && !planner.isSupported;
    }
}

export function isSupported(planner: MinimalPlanner): boolean {
    if (!planner) {
        return false;
    } else if (isChannelAvailable(planner)) {
        return planner.channels![0].isSupported;
    } else {
        return planner.isSupported;
    }
}

export function isGeneralEvent(planner: MinimalPlanner): boolean {
    if (!planner) {
        return false;
    } else if (isChannelAvailable(planner)) {
        return !planner.channels![0].isSelfService && !planner.channels![0].isSupported;
    } else {
        return !planner.isSupported && !planner.isArtifactRequired;
    }
}

export enum PlannerType {
    General,
    Supported,
    SelfService,
    Unknown,
}

export function getPlannerType(planner: Planner): PlannerType {
    if (!planner) {
        throw new Error("Can't get planner type of missing object.");
    }
    if (isSelfService(planner)) {
        return PlannerType.SelfService;
    } else if (isSupported(planner)) {
        return PlannerType.Supported;
    } else if (isGeneralEvent(planner)) {
        return PlannerType.General;
    } else {
        return PlannerType.Unknown;
    }
}

export enum ApplyActionTo {
    all = 'all',
    future = 'future',
    current = 'current',
}

export function linkToPlannerCalendar(dateString: string): RelativeUrl {
    return RelativeUrl.from(`/planner/${dateString}`);
}

export function linkToPlannerEvent(
    clientId: ClientId,
    locationId: LocationId,
    dateString: string,
    plannerId?: number,
): RelativeUrl {
    return RelativeUrl.from(`/planner/${clientId}/${locationId}/${dateString}/${plannerId ? plannerId : 'add'}`);
}

export const PLANNER_EDITABLE_FIELDS = ['title', 'date', 'text', 'instructions', 'status'] as const;
export type PLANNER_EDITABLE_FIELD_TYPE = (typeof PLANNER_EDITABLE_FIELDS)[number];

export { PlannerId } from '@deltasierra/ids';
